import ChannelType from '../enums/loads/load-channel-types.enum';

// Mapeamento de razões de cancelamento para traduções
export const CANCELLATION_REASON_MAP: Record<string, string> = {
    'CancelledByDateOrWeight': 'LoadHistory.Reasons.CancelledByDateOrWeight',
    'Cancelado': 'LoadHistory.Reasons.Cancelado',
    'CanceladoPeloUsuarioTms': 'LoadHistory.Reasons.CanceladoPeloUsuarioTms',
    'LoadCancelEventReceived': 'LoadHistory.Reasons.LoadCancelEventReceived',
    'LoadTenderCancelledEventReceived': 'LoadHistory.Reasons.LoadTenderCancelledEventReceived',
};

/**
 * Função utilitária para obter a mensagem de cancelamento apropriada para won offers
 * @param cancellationReason - Razão de cancelamento original
 * @param channel - Canal da carga
 * @param status - Status da oferta
 * @param t - Função de tradução
 * @returns Mensagem de cancelamento traduzida
 */
export const getWonOfferCancellationReasonMessage = (
    cancellationReason: string | undefined,
    channel: string | undefined,
    status: string,
    t: (key: string) => string,
    emptyField: string = ''
): string => {
    // Se não há razão de cancelamento, retorna campo vazio
    if (!cancellationReason || cancellationReason === emptyField) {
        return emptyField;
    }

    // Verifica se é uma carga de insumos cancelada
    const isInsumosCancelled = (
        (channel === 'Insumos' || channel === ChannelType[ChannelType.Insumos]) &&
        status === 'Canceled'
    );

    // Se não é insumos cancelada, retorna a razão original
    if (!isInsumosCancelled) {
        return cancellationReason;
    }

    // Para cargas de insumos canceladas, verifica se a razão tem tradução específica
    const translationKey = CANCELLATION_REASON_MAP[cancellationReason];
    if (translationKey) {
        return t(translationKey);
    }

    // Se não tem tradução específica, usa a tradução padrão para insumos
    return t('LoadHistory.Reasons.CancelledByDateOrWeight');
};

/**
 * Função utilitária para obter a mensagem de cancelamento apropriada para detalhes de carga
 * @param suspensionReason - Razão de suspensão original
 * @param channel - Canal da carga
 * @param isLoadCancelled - Se a carga está cancelada
 * @param historyEvents - Eventos de histórico
 * @param t - Função de tradução
 * @param suspensionMessage - Função para processar mensagem de suspensão
 * @returns Mensagem de cancelamento traduzida
 */
export const getLoadDetailsCancellationReasonMessage = (
    suspensionReason: string | undefined,
    channel: string | undefined,
    isLoadCancelled: boolean,
    historyEvents: Array<{ eventType: string }> | undefined,
    t: (key: string) => string,
    suspensionMessage: (reason: string | undefined) => string | undefined
): string => {
    // Se não há razão de suspensão, retorna mensagem vazia
    if (!suspensionReason) {
        return '';
    }

    // Verifica se é uma carga de insumos cancelada com evento Unsuspended
    const isInsumosCancelledWithUnsuspended = (
        (channel === 'Insumos' || channel === ChannelType[ChannelType.Insumos]) &&
        isLoadCancelled &&
        historyEvents?.some(ev => ev.eventType === 'Unsuspended')
    );

    // Se não é insumos cancelada com Unsuspended, retorna a razão original
    if (!isInsumosCancelledWithUnsuspended) {
        const processedMessage = suspensionMessage(suspensionReason);
        return processedMessage ?? '';
    }

    // Para cargas de insumos canceladas com Unsuspended, verifica se a razão tem tradução específica
    const translationKey = CANCELLATION_REASON_MAP[suspensionReason];
    if (translationKey) {
        return t(translationKey);
    }

    // Se não tem tradução específica, usa a tradução padrão para insumos
    return t('LoadHistory.Reasons.CancelledByDateOrWeight');
}; 